import { Link } from 'react-router-dom';
import { TriangleAlert } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useVideoStream } from '@/hooks/useStreamingVideos';
import { useVideoFeedFilter } from '@/hooks/useVideoFeedFilter';
import {
  formatDuration,
  getVideoNip19,
  isYouTubeUrl,
  VIDEO_KINDS,
  type VideoData,
} from '@/lib/videoTypes';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { timeAgo } from '@/lib/videoTypes';

function RelatedCard({ video }: { video: VideoData }) {
  const author = useAuthor(video.pubkey);
  const name = author.data?.metadata?.name ?? genUserName(video.pubkey);
  const link = `/${getVideoNip19(video)}`;

  return (
    <div className="flex gap-2 min-w-0">
      <Link to={link} className="relative block w-40 shrink-0 overflow-hidden rounded-lg bg-muted aspect-video">
        {video.contentWarning ? (
          <span className="absolute inset-0 flex items-center justify-center bg-muted">
            <TriangleAlert className="h-5 w-5 text-yellow-500" />
          </span>
        ) : video.thumbnailUrl ? (
          <img src={video.thumbnailUrl} alt="" loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <span className="absolute inset-0 flex items-center justify-center text-xl opacity-40">🎬</span>
        )}
        {video.duration !== undefined && (
          <span className="absolute bottom-1 right-1 rounded bg-black/80 px-1 py-0.5 text-[10px] font-medium text-white tabular-nums">
            {formatDuration(video.duration)}
          </span>
        )}
      </Link>
      <div className="min-w-0 flex-1">
        <Link to={link}>
          <h4 className="line-clamp-2 text-sm font-medium leading-snug hover:text-primary transition-colors">
            {video.title}
          </h4>
        </Link>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{name}</p>
        <p className="text-xs text-muted-foreground">{timeAgo(video.createdAt)}</p>
      </div>
    </div>
  );
}

/** Sidebar list of recent horizontal videos, excluding the one being watched. */
export function RelatedVideos({ currentVideo }: { currentVideo: VideoData }) {
  const stream = useVideoStream({
    kinds: [VIDEO_KINDS.regular.horizontal, VIDEO_KINDS.addressable.horizontal],
  });
  const { filter } = useVideoFeedFilter();

  const videos = stream.videos
    .filter(v => v.id !== currentVideo.id)
    .filter(v => {
      if (!filter.showYouTube && isYouTubeUrl(v.videoUrl)) return false;
      if (!filter.showUnknownDuration && v.duration === undefined) return false;
      return true;
    })
    .slice(0, 12);

  if (stream.isLoading && videos.length === 0) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex gap-2">
            <Skeleton className="w-40 shrink-0 aspect-video rounded-lg" />
            <div className="flex-1 space-y-1.5 py-1">
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (videos.length === 0) return null;

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-muted-foreground">More videos</h2>
      {videos.map((video) => (
        <RelatedCard key={video.id} video={video} />
      ))}
    </div>
  );
}
